import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { ExternalLink, GripVertical, Info, Trash } from "lucide-react"
import { useFieldArray, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CreateWhatsappTemplateSchema, MAX_WHATSAPP_TEMPLATE_URL_BUTTONS } from "schema"
import { z } from "zod"

type TemplateForm = UseFormReturn<z.infer<typeof CreateWhatsappTemplateSchema>>

type Props = {
  form: TemplateForm
  isDisabled: boolean
}

export const WhatsappTemplateButtonsField = ({ form, isDisabled }: Props) => {
  const { t } = useTranslation()
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "template.buttons",
  })
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return

    const activeIndex = fields.findIndex((field) => field.id === active.id)
    const overIndex = fields.findIndex((field) => field.id === over.id)
    if (activeIndex === -1 || overIndex === -1) return

    move(activeIndex, overIndex)
  }

  const addButton = () => {
    append({
      text: "Open link",
      urlType: "DYNAMIC",
      url: undefined,
      variableName: `ticket_url_${fields.length + 1}`,
      variableDescription: "",
    })
  }

  return (
    <div className="@container/footer flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <Label>{t("campaigns.templates.whatsapp.detail.config.blocks.openLinkButton")}</Label>
        <p className="text-sm text-muted-foreground">
          {t("campaigns.templates.whatsapp.detail.config.blocks.openLinkDescription")}
        </p>
      </div>
      {fields.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={fields.map((field) => field.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-2">
              {fields.map((field, index) => (
                <ButtonRow
                  key={field.id}
                  id={field.id}
                  index={index}
                  form={form}
                  isDisabled={isDisabled}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {fields.length < MAX_WHATSAPP_TEMPLATE_URL_BUTTONS && (
        <Button
          className="w-fit"
          type="button"
          variant="outline"
          onClick={addButton}
          disabled={isDisabled}
        >
          <ExternalLink className="w-4 h-4" />
          <span>{t("campaigns.templates.whatsapp.detail.config.blocks.addOpenLinkButton")}</span>
        </Button>
      )}
    </div>
  )
}

type ButtonRowProps = {
  id: string
  index: number
  form: TemplateForm
  isDisabled: boolean
  onRemove: () => void
}

function ButtonRow({ id, index, form, isDisabled, onRemove }: ButtonRowProps) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isDisabled,
  })
  const isStaticButton = form.watch(`template.buttons.${index}.urlType`) === "STATIC"

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={`flex items-start gap-2 bg-card ${isDragging ? "relative z-10" : ""}`}
    >
      <Button
        {...attributes}
        {...listeners}
        type="button"
        variant="link"
        size="icon"
        className="mt-5 shrink-0 cursor-grab active:cursor-grabbing"
        aria-label={t("campaigns.templates.whatsapp.detail.config.blocks.reorderOpenLinkButton")}
        disabled={isDisabled}
      >
        <GripVertical className="size-4" />
      </Button>
      <div
        className={
          isStaticButton
            ? "grid flex-1 grid-cols-1 gap-2 items-start @min-[32rem]/footer:grid-cols-2 @min-[56rem]/footer:grid-cols-[minmax(0,1.2fr)_minmax(10rem,0.6fr)_minmax(0,1.2fr)_auto]"
            : "grid flex-1 grid-cols-1 gap-2 items-start @min-[32rem]/footer:grid-cols-2 @min-[56rem]/footer:grid-cols-[minmax(0,1.2fr)_minmax(10rem,0.6fr)_minmax(0,1.2fr)_repeat(2,minmax(0,1fr))_auto]"
        }
      >
        <FormField
          control={form.control}
          name={`template.buttons.${index}.text`}
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-muted-foreground">
                {t("campaigns.templates.whatsapp.detail.config.blocks.openLinkButtonText")}
              </FormLabel>
              <FormControl>
                <Input {...field} disabled={isDisabled} value={field.value ?? ""} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`template.buttons.${index}.urlType`}
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel className="text-muted-foreground">
                {t("campaigns.templates.whatsapp.detail.config.blocks.buttonType")}
              </FormLabel>
              <Select
                disabled={isDisabled}
                value={field.value}
                onValueChange={(value) => {
                  if (value === "DYNAMIC" || value === "STATIC") {
                    field.onChange(value)
                    form.clearErrors([
                      `template.buttons.${index}.url`,
                      `template.buttons.${index}.variableName`,
                    ])
                  }
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="DYNAMIC">
                      {t("campaigns.templates.whatsapp.detail.config.blocks.dynamicVariable")}
                    </SelectItem>
                    <SelectItem value="STATIC">
                      {t("campaigns.templates.whatsapp.detail.config.blocks.staticVariable")}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={
            isStaticButton
              ? `template.buttons.${index}.url`
              : `template.buttons.${index}.variableName`
          }
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center gap-1.5">
                <FormLabel className="text-muted-foreground">
                  {t(
                    isStaticButton
                      ? "campaigns.templates.whatsapp.detail.config.blocks.openLinkUrl"
                      : "campaigns.templates.whatsapp.detail.config.blocks.openLinkVariableName",
                  )}
                </FormLabel>
                {!isStaticButton && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3.5 w-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(
                        "campaigns.templates.whatsapp.detail.config.blocks.openLinkVariableNameTooltip",
                      )}
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
              <FormControl>
                <Input
                  disabled={isDisabled}
                  type={isStaticButton ? "url" : "text"}
                  value={field.value ?? ""}
                  onChange={(ev) => {
                    const value = ev.target.value
                    field.onChange(
                      isStaticButton
                        ? value
                        : value.toLowerCase().replace(/\s/, "_").replace(/\W+/, ""),
                    )
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!isStaticButton && (
          <FormField
            control={form.control}
            name={`template.buttons.${index}.variableDescription`}
            render={({ field }) => (
              <FormItem className="min-w-0 @min-[56rem]/footer:col-span-2">
                <FormLabel className="text-muted-foreground">
                  {t(
                    "campaigns.templates.whatsapp.detail.config.blocks.templateVariableDescription",
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ""}
                    placeholder={t(
                      "campaigns.templates.whatsapp.detail.config.blocks.templateVariableDescriptionPlaceholder",
                    )}
                    disabled={isDisabled}
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <Button
          size="icon"
          type="button"
          variant="outline"
          onClick={onRemove}
          className="mt-5 justify-self-end"
          disabled={isDisabled}
        >
          <Trash className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
